import { useCallback, useEffect, useState } from "react";

import type { TaskSnapshot } from "../types";

import { getTaskDetailsAsUser } from "../dashboard-client";
import { formatErrorMessage } from "../utils/errors";
import { isHydratedTaskSnapshot } from "../utils/taskSnapshotGuards";
import { useDashboardRealtime } from "./useDashboardRealtime";

export interface TaskDetailsResult {
  data: TaskSnapshot | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

interface TaskDetailsState {
  data: TaskSnapshot | null;
  loading: boolean;
  error: string | null;
}

const INITIAL_STATE: TaskDetailsState = {
  data: null,
  error: null,
  loading: false,
};

export function useTaskDetails(
  taskId: string,
  enabled: boolean,
): TaskDetailsResult {
  const [state, setState] = useState<TaskDetailsState>(INITIAL_STATE);
  const { subscribe } = useDashboardRealtime();

  useEffect(() => {
    if (!taskId) {
      setState(INITIAL_STATE);
      return;
    }
    setState(INITIAL_STATE);
  }, [taskId]);

  const load = useCallback(
    async (force = false) => {
      if (!taskId) {
        return;
      }
      setState((current) => ({
        data: force ? null : current.data,
        error: null,
        loading: true,
      }));

      try {
        const detailsResult = await getTaskDetailsAsUser({ path: { taskId } });
        const snapshot = detailsResult.data.task;
        setState({ data: snapshot, error: null, loading: false });
      } catch (error) {
        setState((current) => ({
          data: current.data,
          error: formatErrorMessage(error),
          loading: false,
        }));
      }
    },
    [taskId],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!taskId) {
      return;
    }
    if (state.data || state.loading) {
      return;
    }
    void load(false);
  }, [enabled, state.data, state.loading, load, taskId]);

  useEffect(() => {
    if (!enabled || !taskId) {
      return;
    }

    const unsubscribe = subscribe(
      (message) => {
        const { type } = message;
        if (type !== "task_snapshot" && type !== "task_callback") {
          return;
        }
        const { payload } = message;
        const { task } = payload;
        if (!isHydratedTaskSnapshot(task)) {
          return;
        }
        if (task.task_id !== taskId) {
          return;
        }
        setState((current) => ({
          ...current,
          data: task,
          error: null,
          loading: false,
        }));
      },
      { topic: "internalEventTopic" },
    );

    return () => unsubscribe();
  }, [enabled, subscribe, taskId]);

  const reload = useCallback(() => {
    void load(true);
  }, [load]);

  return {
    data: state.data,
    error: state.error,
    loading: state.loading,
    reload,
  };
}
